import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

// ─── Types ────────────────────────────────────────────────────────────────────

type Job = {
  id: string;
  type?: string | null;
  status?: string | null;
  payload?: string | null;
  results?: string | null;
  errorDetails?: string | null;
  progress?: number | null;
  totalItems?: number | null;
  processedItems?: number | null;
  createdAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdBy?: string | null;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  PENDING:
    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  PROCESSING:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  COMPLETED:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  FAILED:
    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  PARTIAL_SUCCESS:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
};

const STATUS_ICONS: Record<string, string> = {
  PENDING: '⏳',
  PROCESSING: '⚙️',
  COMPLETED: '✓',
  FAILED: '✗',
  PARTIAL_SUCCESS: '⚠',
};

const TYPE_LABELS: Record<string, string> = {
  RESOLVE_ALERTS: 'Bulk Resolve Alerts',
  FETCH_ALERTS: 'Fetch Alerts',
  PUSH_POLICY: 'Push Policy',
  FETCH_POLICIES: 'Fetch Policies',
  BULK_UPDATE: 'Bulk Update',
  AUDIT_REPORT: 'Audit Report',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseJson(raw: string | null | undefined): any {
  if (!raw) return null;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function duration(start?: string | null, end?: string | null): string | null {
  if (!start || !end) return null;
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}

// ─── Per-Alert Result Row ─────────────────────────────────────────────────────

function AlertResultRow({ result }: { result: { alertId: string; success: boolean; error?: string } }) {
  return (
    <div
      className={`flex items-center gap-2 text-xs rounded px-2 py-1.5 ${
        result.success
          ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
          : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
      }`}
    >
      <span className="font-bold shrink-0">{result.success ? '✓' : '✗'}</span>
      <span className="font-mono truncate flex-1 text-[10px]">{result.alertId}</span>
      {result.error && (
        <span className="text-red-600 dark:text-red-400 truncate max-w-[180px]" title={result.error}>
          {result.error}
        </span>
      )}
    </div>
  );
}

// ─── Job Card ─────────────────────────────────────────────────────────────────

function JobCard({ job }: { job: Job }) {
  const [expanded, setExpanded] = useState(false);

  const results: any[] = parseJson(job.results) || [];
  const payload = parseJson(job.payload);
  const alertCount = payload?.alertIds?.length ?? job.totalItems ?? 0;
  const successCount = Array.isArray(results) ? results.filter((r) => r.success).length : 0;
  const isActive = job.status === 'PENDING' || job.status === 'PROCESSING';
  const elapsed = duration(job.startedAt || job.createdAt, job.completedAt);

  return (
    <div className="card overflow-hidden p-0">
      {/* ── Main row ── */}
      <div className="flex items-center gap-3 p-4">
        {/* Status badge */}
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${
            STATUS_STYLES[job.status || ''] || STATUS_STYLES.PENDING
          }`}
        >
          <span>{STATUS_ICONS[job.status || ''] || '?'}</span>
          {job.status || 'PENDING'}
        </span>

        {/* Job description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white text-sm">
              {TYPE_LABELS[job.type || ''] || job.type || 'Unknown'}
            </span>
            {alertCount > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {alertCount} alert{alertCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400 flex-wrap">
            {job.createdAt && (
              <span>{new Date(job.createdAt).toLocaleString()}</span>
            )}
            {job.createdBy && job.createdBy !== 'unknown' && (
              <span>· by {job.createdBy}</span>
            )}
            {elapsed && <span>· {elapsed}</span>}
          </div>
        </div>

        {/* Right section: progress or result summary */}
        <div className="flex items-center gap-3 shrink-0">
          {/* Progress info for active jobs */}
          {isActive && alertCount > 0 && (
            <div className="w-28 hidden sm:block">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>{job.progress ?? 0}%</span>
                <span>
                  {job.processedItems ?? 0}/{alertCount}
                </span>
              </div>
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${job.progress ?? 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Result summary for completed jobs */}
          {!isActive && Array.isArray(results) && results.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
              {successCount}/{results.length} ok
            </span>
          )}

          {/* Details toggle */}
          {(Array.isArray(results) && results.length > 0) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium shrink-0"
            >
              {expanded ? 'Hide' : 'Details'}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar strip for active jobs */}
      {isActive && (
        <div className="h-0.5 bg-gray-100 dark:bg-gray-700">
          <div
            className="h-full bg-blue-500 transition-all duration-500"
            style={{ width: `${job.progress ?? 0}%` }}
          />
        </div>
      )}

      {/* Expanded per-alert results */}
      {expanded && Array.isArray(results) && results.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700 px-4 pb-4 pt-3">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Per-Alert Results ({successCount}/{results.length} succeeded)
          </p>
          <div className="space-y-1 max-h-52 overflow-y-auto pr-1">
            {results.map((r: any, i: number) => (
              <AlertResultRow key={i} result={r} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function JobQueue() {
  const { data: jobs, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const result = await client.models.Job.list();
      return (result.data as unknown as Job[])
        .slice()
        .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
    },
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const activeJobs = (jobs || []).filter(
    (j) => j.status === 'PENDING' || j.status === 'PROCESSING'
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Background Jobs</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {activeJobs.length > 0
              ? `${activeJobs.length} job${activeJobs.length !== 1 ? 's' : ''} running — auto-refreshing every 10 seconds`
              : dataUpdatedAt
                ? `Last updated ${new Date(dataUpdatedAt).toLocaleTimeString()} — auto-refreshes every 10 seconds`
                : 'Auto-refreshes every 10 seconds'}
          </p>
        </div>
      </div>

      {/* Status summary chips */}
      {jobs && jobs.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(['COMPLETED', 'PARTIAL_SUCCESS', 'FAILED', 'PROCESSING', 'PENDING'] as const).map((status) => {
            const count = jobs.filter((j) => j.status === status).length;
            if (count === 0) return null;
            return (
              <span
                key={status}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}
              >
                {STATUS_ICONS[status]} {count} {status.toLowerCase().replace('_', ' ')}
              </span>
            );
          })}
        </div>
      )}

      {/* Job list */}
      {isLoading ? (
        <div className="card p-8 text-center text-gray-500">Loading jobs...</div>
      ) : !jobs || jobs.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">No jobs yet.</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Jobs are created when you use Bulk Resolve Alerts or other async operations.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => (
            <JobCard key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}
